import { useState, useEffect, useCallback } from 'react';
import { resolveWebAppUrl } from '../../../config/api';
import {
  Server, Play, CheckCircle2, XCircle, Clock, Zap,
  FlaskConical, Terminal, RefreshCw, Loader2,
} from 'lucide-react';

// ── Design tokens (matches PlatformHealthDashboard) ──

const C = {
  text: '#18181B',
  textSec: '#52525B',
  textMuted: '#A1A1AA',
  rose: '#E11D48',
  roseSoft: '#FFF1F2',
  pass: '#059669',
  passBg: '#D1FAE5',
  warn: '#B45309',
  warnBg: '#FEF3C7',
  fail: '#E11D48',
  failBg: '#FFF1F2',
  bg: '#FAFAFA',
  bgCard: '#FFFFFF',
  border: '#E4E4E7',
  mono: "'JetBrains Mono', monospace",
  sans: "'DM Sans', sans-serif",
  serif: "'Instrument Serif', serif",
};

// ── Types ──

interface HealthData {
  status: string;
  service: string;
  version: string;
  biomarkers: number;
  correlations: number;
  dataVersion: string;
}

interface McpTool {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
}

interface TestResult {
  name: string;
  passed: boolean;
  detail: string;
  durationMs: number;
}

interface ToolCallResult {
  tool: string;
  input: Record<string, unknown>;
  output: Record<string, unknown> | null;
  error: string | null;
  durationMs: number;
}

// ── Constants ──

const DEFAULT_URL = 'http://localhost:8080';

const HEADERS = {
  'Content-Type': 'application/json',
  'Accept': 'application/json, text/event-stream',
};

const TABS = [
  { key: 'status', label: 'Status', icon: Server },
  { key: 'tests', label: 'Smoke Tests', icon: FlaskConical },
  { key: 'explorer', label: 'Tool Explorer', icon: Terminal },
] as const;

type TabKey = typeof TABS[number]['key'];

// ── SSE Parser ──

function parseSseData(raw: string): unknown {
  for (const line of raw.split('\n')) {
    if (line.startsWith('data: ')) {
      return JSON.parse(line.slice(6));
    }
  }
  throw new Error(`No data line in SSE response: ${raw.slice(0, 200)}`);
}

// ── Tool presets for explorer ──

const TOOL_PRESETS: Record<string, { label: string; args: Record<string, unknown> }[]> = {
  hemologix_get_optimal_ranges: [
    { label: 'TSH + Vitamin D + Ferritin', args: { biomarkers: ['TSH', 'Vitamin D', 'Ferritin'] } },
    { label: 'Thyroid panel', args: { category: 'thyroid' } },
    { label: 'Female hormones', args: { gender: 'female', category: 'hormones_women' } },
    { label: 'Tier 1 basics', args: { tier: 1 } },
    { label: 'All biomarkers', args: {} },
  ],
  hemologix_get_correlations: [
    { label: 'TSH correlations', args: { biomarker: 'TSH' } },
    { label: 'Ferritin correlations', args: { biomarker: 'Ferritin' } },
    { label: 'Strong only', args: { strength: 'strong' } },
    { label: 'Inverse relationships', args: { relationship: 'inverse' } },
    { label: 'All correlations', args: {} },
  ],
  hemologix_get_gap_analysis: [
    { label: 'Test BAA gate', args: { session_token: 'test-token-123' } },
  ],
  hemologix_get_protocol: [
    { label: 'Test BAA gate', args: { session_token: 'test-token-123' } },
  ],
  hemologix_get_retest_plan: [
    { label: 'Test BAA gate', args: { session_token: 'test-token-123' } },
  ],
  hemologix_initiate_consultation: [
    { label: 'Gate 2 eval', args: { session_token: 'test-token-123', reason: 'GATE_2_EVAL' } },
    { label: 'Protocol review', args: { session_token: 'test-token-123', reason: 'PROTOCOL_REVIEW' } },
    { label: 'Lab follow-up', args: { session_token: 'test-token-123', reason: 'LAB_FOLLOWUP' } },
  ],
};

// ── Smoke Test Definitions ──

interface SmokeTest {
  name: string;
  run: (base: string) => Promise<void>;
}

function assert(condition: boolean, msg: string) {
  if (!condition) throw new Error(msg);
}

async function mcpCall(base: string, method: string, params: Record<string, unknown>): Promise<any> {
  const res = await fetch(`${base}/mcp`, {
    method: 'POST',
    headers: HEADERS,
    body: JSON.stringify({ jsonrpc: '2.0', id: 1, method, params }),
  });
  return parseSseData(await res.text());
}

async function toolCall(base: string, name: string, args: Record<string, unknown>): Promise<Record<string, unknown>> {
  const response = await mcpCall(base, 'tools/call', { name, arguments: args }) as any;
  const text = response?.result?.content?.[0]?.text;
  if (!text) throw new Error(`No content: ${JSON.stringify(response)}`);
  return JSON.parse(text);
}

const SMOKE_TESTS: SmokeTest[] = [
  {
    name: 'Health check',
    run: async (base) => {
      const res = await fetch(`${base}/health`);
      const d = await res.json() as HealthData;
      assert(d.status === 'healthy', `Status: ${d.status}`);
      assert(d.biomarkers > 100, `Biomarkers: ${d.biomarkers}`);
    },
  },
  {
    name: 'MCP initialize',
    run: async (base) => {
      const r = await mcpCall(base, 'initialize', {
        protocolVersion: '2025-03-26', capabilities: {},
        clientInfo: { name: 'admin-panel', version: '1.0.0' },
      }) as any;
      assert(r.result?.serverInfo?.name === 'hemologix', `Server: ${r.result?.serverInfo?.name}`);
    },
  },
  {
    name: 'Tools list (6 tools)',
    run: async (base) => {
      const r = await mcpCall(base, 'tools/list', {}) as any;
      assert(r.result?.tools?.length === 6, `Tool count: ${r.result?.tools?.length}`);
    },
  },
  {
    name: 'Optimal ranges — specific lookup',
    run: async (base) => {
      const d = await toolCall(base, 'hemologix_get_optimal_ranges', { biomarkers: ['TSH', 'Ferritin'] });
      assert(d.count === 2, `Count: ${d.count}`);
    },
  },
  {
    name: 'Optimal ranges — category filter',
    run: async (base) => {
      const d = await toolCall(base, 'hemologix_get_optimal_ranges', { category: 'thyroid' });
      assert((d.count as number) >= 3, `Thyroid count: ${d.count}`);
    },
  },
  {
    name: 'Optimal ranges — not found handling',
    run: async (base) => {
      const d = await toolCall(base, 'hemologix_get_optimal_ranges', { biomarkers: ['TSH', 'Fake_XYZ'] });
      assert((d.notFound as string[])?.includes('Fake_XYZ'), 'Missing notFound');
    },
  },
  {
    name: 'Correlations — by biomarker',
    run: async (base) => {
      const d = await toolCall(base, 'hemologix_get_correlations', { biomarker: 'TSH' });
      assert((d.count as number) >= 1, `TSH correlations: ${d.count}`);
    },
  },
  {
    name: 'Correlations — by strength',
    run: async (base) => {
      const d = await toolCall(base, 'hemologix_get_correlations', { strength: 'strong' });
      assert((d.count as number) >= 1, `Strong count: ${d.count}`);
    },
  },
  {
    name: 'Gap analysis — BAA_PENDING',
    run: async (base) => {
      const d = await toolCall(base, 'hemologix_get_gap_analysis', { session_token: 'test' });
      assert(d.status === 'BAA_PENDING', `Status: ${d.status}`);
    },
  },
  {
    name: 'Protocol — BAA_PENDING + DSHEA',
    run: async (base) => {
      const d = await toolCall(base, 'hemologix_get_protocol', { session_token: 'test' });
      assert(d.status === 'BAA_PENDING', `Status: ${d.status}`);
      assert((d.disclaimer as string)?.includes('FDA'), 'Missing DSHEA');
    },
  },
  {
    name: 'Retest plan — BAA_PENDING',
    run: async (base) => {
      const d = await toolCall(base, 'hemologix_get_retest_plan', { session_token: 'test' });
      assert(d.status === 'BAA_PENDING', `Status: ${d.status}`);
    },
  },
  {
    name: 'Consultation — BAA_PENDING',
    run: async (base) => {
      const d = await toolCall(base, 'hemologix_initiate_consultation', { session_token: 'test', reason: 'GATE_2_EVAL' });
      assert(d.status === 'BAA_PENDING', `Status: ${d.status}`);
    },
  },
  {
    name: 'GET /mcp — 405',
    run: async (base) => {
      const res = await fetch(`${base}/mcp`);
      assert(res.status === 405, `Status: ${res.status}`);
    },
  },
  {
    name: 'CORS — allowed origin',
    run: async (base) => {
      const origin = resolveWebAppUrl();
      const res = await fetch(`${base}/health`, {
        headers: { Origin: origin },
      });
      const h = res.headers.get('access-control-allow-origin');
      assert(h === origin, `CORS: ${h}`);
    },
  },
];

// ══════════════════════════════════════════════════════════
// ── Component ──
// ══════════════════════════════════════════════════════════

export function McpDashboard() {
  const [activeTab, setActiveTab] = useState<TabKey>('status');
  const [baseUrl, setBaseUrl] = useState(DEFAULT_URL);

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto' }}>
      <h2 style={{ fontFamily: C.serif, fontSize: 24, color: C.text, marginBottom: 4 }}>
        MCP Server
      </h2>
      <p style={{ fontFamily: C.sans, fontSize: 14, color: C.textMuted, marginBottom: 16 }}>
        HemoLogix Model Context Protocol — tool registry, smoke tests, explorer
      </p>

      {/* Server URL bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8,
        marginBottom: 20, padding: '8px 12px',
        background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4,
      }}>
        <Server size={16} style={{ color: C.textMuted, flexShrink: 0 }} />
        <input
          type="text"
          value={baseUrl}
          onChange={(e) => setBaseUrl(e.target.value.replace(/\/+$/, ''))}
          style={{
            flex: 1, border: 'none', outline: 'none', background: 'none',
            fontFamily: C.mono, fontSize: 13, color: C.text,
          }}
          placeholder="http://localhost:8080"
        />
      </div>

      {/* Tab bar */}
      <div style={{
        display: 'flex', gap: 4,
        borderBottom: `1px solid ${C.border}`, marginBottom: 24, overflowX: 'auto',
      }}>
        {TABS.map((tab) => {
          const isActive = activeTab === tab.key;
          const Icon = tab.icon;
          return (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6,
                padding: '10px 16px', fontSize: 14, fontFamily: C.sans,
                fontWeight: isActive ? 600 : 400,
                color: isActive ? C.rose : C.textSec,
                background: 'none', border: 'none',
                borderBottom: isActive ? `2px solid ${C.rose}` : '2px solid transparent',
                cursor: 'pointer', whiteSpace: 'nowrap',
              }}
            >
              <Icon size={16} />
              {tab.label}
            </button>
          );
        })}
      </div>

      {activeTab === 'status' && <StatusTab baseUrl={baseUrl} />}
      {activeTab === 'tests' && <SmokeTestsTab baseUrl={baseUrl} />}
      {activeTab === 'explorer' && <ToolExplorerTab baseUrl={baseUrl} />}
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ── Status Tab ──
// ══════════════════════════════════════════════════════════

function StatusTab({ baseUrl }: { baseUrl: string }) {
  const [health, setHealth] = useState<HealthData | null>(null);
  const [tools, setTools] = useState<McpTool[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [healthRes, toolsRes] = await Promise.all([
        fetch(`${baseUrl}/health`).then(r => r.json()) as Promise<HealthData>,
        mcpCall(baseUrl, 'tools/list', {}).then((r: any) => r.result?.tools ?? []),
      ]);
      setHealth(healthRes);
      setTools(toolsRes);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Connection failed');
      setHealth(null);
      setTools([]);
    } finally {
      setLoading(false);
    }
  }, [baseUrl]);

  useEffect(() => { refresh(); }, [refresh]);

  if (error) {
    return (
      <div>
        <ErrorCard message={error} />
        <button onClick={refresh} style={btnStyle}>
          <RefreshCw size={14} /> Retry
        </button>
      </div>
    );
  }

  if (loading || !health) {
    return <LoadingState label="Connecting to MCP server..." />;
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
        <button onClick={refresh} style={btnStyle}>
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Health stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(180px, 1fr))', gap: 12, marginBottom: 28 }}>
        <StatCard label="Status" value={health.status} color={health.status === 'healthy' ? C.pass : C.fail} />
        <StatCard label="Version" value={health.version} />
        <StatCard label="Biomarkers" value={String(health.biomarkers)} />
        <StatCard label="Correlations" value={String(health.correlations)} />
        <StatCard label="Data Version" value={health.dataVersion} />
      </div>

      {/* Tool registry */}
      <h3 style={{ fontFamily: C.serif, fontSize: 18, color: C.text, marginBottom: 12 }}>
        Tool Registry ({tools.length})
      </h3>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {tools.map((tool) => {
          const isPhase1 = !tool.name.includes('gap') && !tool.name.includes('protocol')
            && !tool.name.includes('retest') && !tool.name.includes('consultation');
          return (
            <div key={tool.name} style={{
              background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4,
              padding: '12px 16px',
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{
                  fontFamily: C.mono, fontSize: 13, fontWeight: 600, color: C.text,
                }}>{tool.name}</span>
                <span style={{
                  fontSize: 11, fontWeight: 600, borderRadius: 4,
                  padding: '2px 8px',
                  background: isPhase1 ? C.passBg : C.warnBg,
                  color: isPhase1 ? C.pass : C.warn,
                }}>
                  {isPhase1 ? 'LIVE' : 'BAA_PENDING'}
                </span>
              </div>
              <p style={{ fontFamily: C.sans, fontSize: 13, color: C.textSec, margin: 0, lineHeight: 1.5 }}>
                {tool.description.length > 160 ? tool.description.slice(0, 160) + '...' : tool.description}
              </p>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ── Smoke Tests Tab ──
// ══════════════════════════════════════════════════════════

function SmokeTestsTab({ baseUrl }: { baseUrl: string }) {
  const [results, setResults] = useState<TestResult[]>([]);
  const [running, setRunning] = useState(false);
  const [currentTest, setCurrentTest] = useState<string | null>(null);

  const runAll = useCallback(async () => {
    setRunning(true);
    setResults([]);

    const collected: TestResult[] = [];

    for (const test of SMOKE_TESTS) {
      setCurrentTest(test.name);
      const start = Date.now();
      try {
        await test.run(baseUrl);
        collected.push({ name: test.name, passed: true, detail: 'OK', durationMs: Date.now() - start });
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : String(err);
        collected.push({ name: test.name, passed: false, detail: msg, durationMs: Date.now() - start });
      }
      setResults([...collected]);
    }

    setCurrentTest(null);
    setRunning(false);
  }, [baseUrl]);

  const passed = results.filter(r => r.passed).length;
  const failed = results.filter(r => !r.passed).length;
  const totalMs = results.reduce((sum, r) => sum + r.durationMs, 0);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 20 }}>
        <button onClick={runAll} disabled={running} style={{
          ...btnStyle,
          background: running ? C.border : C.rose,
          color: '#fff',
          cursor: running ? 'default' : 'pointer',
        }}>
          {running ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Play size={14} />}
          {running ? 'Running...' : 'Run All Tests'}
        </button>

        {results.length > 0 && (
          <span style={{ fontFamily: C.sans, fontSize: 14, color: C.textSec }}>
            {passed} passed, {failed} failed — {totalMs}ms
          </span>
        )}
      </div>

      {/* Progress indicator */}
      {running && currentTest && (
        <div style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '8px 12px', marginBottom: 16,
          background: C.roseSoft, borderRadius: 4, fontSize: 13, color: C.rose,
        }}>
          <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} />
          Running: {currentTest}
        </div>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          {results.map((r, i) => (
            <div key={i} style={{
              display: 'flex', alignItems: 'center', gap: 8,
              padding: '8px 12px',
              background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4,
            }}>
              {r.passed
                ? <CheckCircle2 size={16} style={{ color: C.pass, flexShrink: 0 }} />
                : <XCircle size={16} style={{ color: C.fail, flexShrink: 0 }} />
              }
              <span style={{ fontFamily: C.sans, fontSize: 13, color: C.text, flex: 1 }}>
                {r.name}
              </span>
              {!r.passed && (
                <span style={{ fontFamily: C.mono, fontSize: 12, color: C.fail }}>
                  {r.detail}
                </span>
              )}
              <span style={{ fontFamily: C.mono, fontSize: 12, color: C.textMuted, flexShrink: 0 }}>
                {r.durationMs}ms
              </span>
            </div>
          ))}
        </div>
      )}

      {/* Score bar */}
      {results.length > 0 && !running && (
        <div style={{ marginTop: 16, padding: '12px 16px', background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
            <span style={{ fontFamily: C.sans, fontSize: 13, fontWeight: 600, color: C.text }}>
              Score: {results.length > 0 ? Math.round((passed / results.length) * 100) : 0}%
            </span>
            <span style={{ fontFamily: C.mono, fontSize: 13, color: C.textMuted }}>
              {passed}/{results.length}
            </span>
          </div>
          <div style={{ height: 6, background: C.border, borderRadius: 3 }}>
            <div style={{
              height: 6, borderRadius: 3,
              width: `${results.length > 0 ? (passed / results.length) * 100 : 0}%`,
              background: failed === 0 ? C.pass : failed <= 2 ? C.warn : C.fail,
              transition: 'width 0.3s ease',
            }} />
          </div>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ── Tool Explorer Tab ──
// ══════════════════════════════════════════════════════════

function ToolExplorerTab({ baseUrl }: { baseUrl: string }) {
  const [tools, setTools] = useState<McpTool[]>([]);
  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const [inputJson, setInputJson] = useState('{}');
  const [result, setResult] = useState<ToolCallResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [toolsLoading, setToolsLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const r = await mcpCall(baseUrl, 'tools/list', {}) as any;
        setTools(r.result?.tools ?? []);
        if (r.result?.tools?.length > 0) {
          setSelectedTool(r.result.tools[0].name);
        }
      } catch {
        setTools([]);
      } finally {
        setToolsLoading(false);
      }
    })();
  }, [baseUrl]);

  const callTool = useCallback(async () => {
    if (!selectedTool) return;
    setLoading(true);
    const start = Date.now();
    try {
      const args = JSON.parse(inputJson);
      const data = await toolCall(baseUrl, selectedTool, args);
      setResult({
        tool: selectedTool, input: args, output: data, error: null,
        durationMs: Date.now() - start,
      });
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : String(err);
      setResult({
        tool: selectedTool, input: {}, output: null, error: msg,
        durationMs: Date.now() - start,
      });
    } finally {
      setLoading(false);
    }
  }, [baseUrl, selectedTool, inputJson]);

  const presets = selectedTool ? (TOOL_PRESETS[selectedTool] ?? []) : [];

  if (toolsLoading) return <LoadingState label="Loading tool registry..." />;

  return (
    <div>
      {/* Tool selector */}
      <div style={{ marginBottom: 16 }}>
        <label style={{ fontFamily: C.sans, fontSize: 13, color: C.textSec, display: 'block', marginBottom: 4 }}>
          Tool
        </label>
        <select
          value={selectedTool ?? ''}
          onChange={(e) => {
            setSelectedTool(e.target.value);
            setInputJson('{}');
            setResult(null);
          }}
          style={{
            width: '100%', padding: '8px 12px', borderRadius: 4,
            border: `1px solid ${C.border}`, background: C.bgCard,
            fontFamily: C.mono, fontSize: 13, color: C.text,
          }}
        >
          {tools.map(t => (
            <option key={t.name} value={t.name}>{t.name}</option>
          ))}
        </select>
      </div>

      {/* Presets */}
      {presets.length > 0 && (
        <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
          {presets.map((p, i) => (
            <button
              key={i}
              onClick={() => setInputJson(JSON.stringify(p.args, null, 2))}
              style={{
                padding: '4px 10px', borderRadius: 4, fontSize: 12,
                border: `1px solid ${C.border}`, background: C.bgCard,
                fontFamily: C.sans, color: C.textSec, cursor: 'pointer',
              }}
            >
              {p.label}
            </button>
          ))}
        </div>
      )}

      {/* Input editor */}
      <div style={{ marginBottom: 12 }}>
        <label style={{ fontFamily: C.sans, fontSize: 13, color: C.textSec, display: 'block', marginBottom: 4 }}>
          Arguments (JSON)
        </label>
        <textarea
          value={inputJson}
          onChange={(e) => setInputJson(e.target.value)}
          rows={5}
          style={{
            width: '100%', padding: '8px 12px', borderRadius: 4,
            border: `1px solid ${C.border}`, background: C.bgCard,
            fontFamily: C.mono, fontSize: 13, color: C.text, resize: 'vertical',
            boxSizing: 'border-box',
          }}
        />
      </div>

      {/* Call button */}
      <button
        onClick={callTool}
        disabled={loading || !selectedTool}
        style={{
          ...btnStyle,
          background: loading ? C.border : C.rose,
          color: '#fff',
          cursor: loading ? 'default' : 'pointer',
          marginBottom: 20,
        }}
      >
        {loading ? <Loader2 size={14} style={{ animation: 'spin 1s linear infinite' }} /> : <Zap size={14} />}
        {loading ? 'Calling...' : 'Call Tool'}
      </button>

      {/* Result */}
      {result && (
        <div style={{
          background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4,
          overflow: 'hidden',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            padding: '8px 12px', borderBottom: `1px solid ${C.border}`,
            background: result.error ? C.failBg : C.passBg,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              {result.error
                ? <XCircle size={14} style={{ color: C.fail }} />
                : <CheckCircle2 size={14} style={{ color: C.pass }} />
              }
              <span style={{ fontFamily: C.mono, fontSize: 12, color: result.error ? C.fail : C.pass }}>
                {result.error ? 'ERROR' : 'OK'}
              </span>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
              <Clock size={12} style={{ color: C.textMuted }} />
              <span style={{ fontFamily: C.mono, fontSize: 12, color: C.textMuted }}>
                {result.durationMs}ms
              </span>
            </div>
          </div>
          <pre style={{
            padding: 12, margin: 0, overflowX: 'auto',
            fontFamily: C.mono, fontSize: 12, lineHeight: 1.5, color: C.text,
            maxHeight: 400,
          }}>
            {result.error
              ? result.error
              : JSON.stringify(result.output, null, 2)
            }
          </pre>
        </div>
      )}

      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}

// ══════════════════════════════════════════════════════════
// ── Shared sub-components ──
// ══════════════════════════════════════════════════════════

function StatCard({ label, value, color }: { label: string; value: string; color?: string }) {
  return (
    <div style={{
      background: C.bgCard, border: `1px solid ${C.border}`, borderRadius: 4,
      padding: '12px 16px',
    }}>
      <div style={{ fontFamily: C.sans, fontSize: 12, color: C.textMuted, marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ fontFamily: C.mono, fontSize: 18, fontWeight: 600, color: color ?? C.text }}>
        {value}
      </div>
    </div>
  );
}

function ErrorCard({ message }: { message: string }) {
  return (
    <div style={{
      padding: '12px 16px', marginBottom: 16,
      background: C.failBg, border: `1px solid ${C.fail}20`, borderRadius: 4,
    }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        <XCircle size={16} style={{ color: C.fail }} />
        <span style={{ fontFamily: C.sans, fontSize: 14, color: C.fail, fontWeight: 500 }}>
          Connection Failed
        </span>
      </div>
      <p style={{ fontFamily: C.mono, fontSize: 12, color: C.textSec, margin: '8px 0 0' }}>
        {message}
      </p>
    </div>
  );
}

function LoadingState({ label }: { label: string }) {
  return (
    <div style={{ textAlign: 'center', padding: 60, color: C.textMuted, fontSize: 14, fontFamily: C.sans }}>
      <Loader2 size={24} style={{ animation: 'spin 1s linear infinite', marginBottom: 12 }} />
      <div>{label}</div>
      <style>{`@keyframes spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  );
}

const btnStyle: React.CSSProperties = {
  display: 'inline-flex', alignItems: 'center', gap: 6,
  padding: '8px 16px', borderRadius: 4,
  border: `1px solid ${C.border}`, background: C.bgCard,
  fontFamily: C.sans, fontSize: 13, fontWeight: 500,
  color: C.text, cursor: 'pointer',
};
